// @ts-ignore
import { useEffect, useMemo, useState, useCallback } from "react";
import { getPoolSettings } from "../util";
import { ChainId, type NftPoolConfig, type PoolId, chainSettings, fallbackMulticall, type RequiredNftPoolChainType, chainDefaults } from "../constants";
import { type Address, createPublicClient, extractChain, http, parseAbiItem } from "viem";
import { useReadContracts, useConnection, useReadContract } from "wagmi";
import { nftAbi } from "../constants/abi";
import { createRateLimitedClient } from "@/util/viem-batching";
import * as chains from 'viem/chains';

// ERC721 Transfer event ABI
const ERC721_TRANSFER_EVENT = parseAbiItem('event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)');
/**
 * Custom hook to fetch the user's NFT balance in an array of tokenId's for a specific pool.
 * @param poolId - The ID of the pool.
 * @param balance - User's total NFT balanceOf.
 * @param account - (Optional) The account address.
 * @returns An object containing the NFT token IDs and a refetch function.
 */
export function useNftBalance(poolId: PoolId | number, balance: number, account?: Address, chainId?: ChainId) {
  const {
    chainId: currChainId
  } = useConnection();
  const currPool: NftPoolConfig = useMemo(() => getPoolSettings(chainId as RequiredNftPoolChainType ?? ChainId.SHIBARIUM as RequiredNftPoolChainType, poolId ?? 1), [chainId, poolId]);
  const [myTokenIds, setMyTokenIds] = useState<Array<number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    data: isEnumerableData
  } = useReadContract({
    address: currPool.nftAddress,
    abi: nftAbi,
    functionName: "supportsInterface",
    args: ['0x780e9d63']
  }) as {
    data: boolean | undefined;
  };
  const isEnumerable = Boolean(isEnumerableData);
  console.log("Contract isEnumerable", isEnumerable);
  const nftContract = useMemo(() => ({
    address: currPool.nftAddress,
    abi: nftAbi
  }) as const, [currPool.nftAddress]);
  const idReads = useMemo(() => {
    if (!isEnumerable || !account || balance <= 0) return [];
    return Array.from({
      length: balance
    }, (_, i) => ({
      ...nftContract,
      functionName: "tokenOfOwnerByIndex" as const,
      args: [account.toString(), i] as const
    }));
  }, [isEnumerable, account, balance, nftContract]);

  // Get chain configuration
  const viemChain = extractChain({
    chains: Object.values(chains),
    id: (currChainId ?? chainId ?? ChainId.SHIBARIUM) as 109 | 2000
  });

  // Create a public client for the chain
  const publicClient = createPublicClient({
    chain: viemChain,
    transport: http("https://rpc.shibarium.shib.io")
  });

  // Fetch transfer logs
  const fetchTransferLogs = useCallback(async () => {
    if (!publicClient || !account) {
      console.log("WTF MAN No Client?!");
      return;
    }
    try {
      setIsLoading(true);

      // Get current block number
      const currentBlock = await publicClient.getBlockNumber();
      // Start from 1 month ago (roughly 2.5M blocks)
      const fromBlock = currentBlock - 6500000n;
      const BLOCK_RANGE = 20000n;
      console.log("Start to fetch transfer logs");
      const fetchLogsInRange = async (startBlock: bigint, endBlock: bigint) => {
        return publicClient.getLogs({
          address: currPool.nftAddress,
          event: ERC721_TRANSFER_EVENT,
          args: {
            from: account
          },
          fromBlock: startBlock,
          toBlock: endBlock
        }).catch(err => {
          console.warn(`Error fetching transfers for blocks ${startBlock}-${endBlock}:`, err);
          return [];
        });
      };
      const fetchAllLogs = async (isOutgoing: boolean) => {
        let allLogs: any[] = [];
        let currentStart = fromBlock;
        while (currentStart < currentBlock) {
          const endBlock_0 = currentStart + BLOCK_RANGE > currentBlock ? currentBlock : currentStart + BLOCK_RANGE;
          const logs = await fetchLogsInRange(currentStart, endBlock_0);
          allLogs = [...allLogs, ...logs];
          currentStart = endBlock_0 + 1n;
        }
        return allLogs;
      };
      const [outLogs, inLogs] = await Promise.all([fetchAllLogs(true), fetchAllLogs(false)]);

      // Merge & sort logs
      const allLogs_0 = [...outLogs, ...inLogs].sort((a, b) => a.blockNumber === b.blockNumber ? Number(a.logIndex - b.logIndex) : Number(a.blockNumber - b.blockNumber));
      console.log(allLogs_0);

      // Replay transfers to get current ownership
      const owned = new Set<bigint>();
      for (const log of allLogs_0) {
        const from = log.args.from;
        const to = log.args.to;
        const tokenId = log.args.tokenId;
        if (typeof tokenId === 'bigint') {
          if (to?.toLowerCase() === account.toLowerCase()) owned.add(tokenId);
          if (from?.toLowerCase() === account.toLowerCase()) owned.delete(tokenId);
        }
      }
      setMyTokenIds(Array.from(owned).map(Number));
    } catch (error) {
      console.error('Error fetching transfer logs:', error);
      setMyTokenIds([]);
    } finally {
      setIsLoading(false);
    }
  }, [publicClient, account, currPool.nftAddress]);
  const {
    data: readData,
    isError,
    refetch: refetchEnumerable
  } = useReadContracts({
    allowFailure: false,
    contracts: idReads,
    query: {
      enabled: Boolean(isEnumerable && account && balance > 0)
    },
    multicallAddress: (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall
  }) as {
    data: bigint[] | undefined;
    isError: boolean;
    refetch: () => void;
  };
  const refresh = useCallback(() => {
    if (isEnumerable) {
      refetchEnumerable();
    } else {
      fetchTransferLogs();
    }
  }, [isEnumerable, refetchEnumerable, fetchTransferLogs]);
  useEffect(() => {
    if (!account || balance === 0) {
      setMyTokenIds([]);
      return;
    }
    if (readData && isEnumerable) {
      const tokenIdArray: Array<number> = [];
      for (let i_0 = 0; i_0 < readData.length; i_0++) {
        tokenIdArray[i_0] = Number(readData[i_0]);
      }
      setMyTokenIds(tokenIdArray);
    }
  }, [readData, balance, isEnumerable, account]);

  // Fetch transfer logs when needed
  useEffect(() => {
    if (!isEnumerable && account && balance > 0) {
      fetchTransferLogs();
    }
  }, [isEnumerable, account, balance, publicClient, fetchTransferLogs]);
  return {
    myTokenIds,
    refetch: refresh,
    isLoading
  };
}